import { StageDef } from '../automationMachineInterfaces'
import { STRAVA_AUTOMATION_STAGE_STATUSES } from './stravaAutomationStageStatuses'
import * as stravaUrls from './stravaUrls'
import { StravaSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { evaluateXpath } from '../xpath'

const MIN_DISTANCE = 800
const MAX_DISTANCE = 1600

// Settings properties
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(
    key,
    (value) => `${(value / MAX_DISTANCE).toString()} miles`,
  )

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => value >= MIN_DISTANCE)

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// Setup gather stage definitions

enum StageKey {
  GATHER_SETTING_MAP_VISIBILITY = 'GATHER_SETTING_MAP_VISIBILITY',
  UPDATE_SETTING_MAP_VISIBILITY = 'UPDATE_SETTING_MAP_VISIBILITY',
  UNDO_SETTING_MAP_VISIBILITY = 'UNDO_SETTING_MAP_VISIBILITY',
  GATHER_SETTING_MAP_VISIBILITY_FINISH = 'GATHER_SETTING_MAP_VISIBILITY_FINISH',
  UPDATE_SETTING_MAP_VISIBILITY_FINISH = 'UPDATE_SETTING_MAP_VISIBILITY_FINISH',
  UNDO_SETTING_MAP_VISIBILITY_FINISH = 'UNDO_SETTING_MAP_VISIBILITY_FINISH',
}

function getSlider() {
  const sliderXpath = "//button[@role='slider']"

  return evaluateXpath<HTMLButtonElement>(document, sliderXpath)[0]
}

async function getCurrentDistance() {
  const sliderButton = getSlider()
  if (sliderButton) {
    return parseInt(sliderButton.getAttribute('aria-valuenow'), 10)
  }

  throw new Error('Could not find slider')
}

async function updateCurrentDistance(newDistance) {
  const metaElement = document.getElementsByName(
    'csrf-token',
  )[0] as HTMLMetaElement

  const payload =
    newDistance > 0
      ? {
          settings: {
            default_stream_privacy: `meters_${newDistance}`,
          },
        }
      : {
          settings: {
            default_stream_privacy: 'disabled',
          },
        }

  const response = await fetch(`https://www.strava.com/athlete_settings`, {
    method: 'PUT',
    headers: {
      Accept: 'text/javascript',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-TOKEN': metaElement.content,
      'Content-Type': 'application/json;charset=UTF-8',
    },
    body: JSON.stringify(payload),
  })

  if (response.status !== 200) {
    throw new Error(
      `Web request to update ${JSON.stringify(payload)} not successful`,
    )
  }
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_MAP_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: stravaUrls.startEndLocationUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext
      const currentDistance = await getCurrentDistance()
      await updateStoredInitialValues(
        {
          [StravaSettingKey.MAP_VISIBILITY]: currentDistance,
        },
        enums.StravaRecommendationKey.STRAVA_MAP_VISIBILITY,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_MAP_VISIBILITY_FINISH,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: stravaUrls.startEndLocationUrl,
    action: async () => {
      window.location.reload()
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_MAP_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: stravaUrls.startEndLocationUrl,
    action: async (machineContext, userContext) => {
      const { isDry } = userContext
      const currentDistance = await getCurrentDistance()

      if (!isDry && currentDistance < MIN_DISTANCE) {
        await updateCurrentDistance(MIN_DISTANCE)
      }
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_MAP_VISIBILITY_FINISH,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: stravaUrls.startEndLocationUrl,
    action: async () => {
      window.location.reload()
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_MAP_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: stravaUrls.startEndLocationUrl,
    action: async (machineContext, userContext) => {
      const { isDry, initialValues } = userContext
      const currentDistance = await getCurrentDistance()

      if (
        !initialValues ||
        !initialValues[StravaSettingKey.MAP_VISIBILITY] === undefined ||
        !initialValues[StravaSettingKey.MAP_VISIBILITY] === null
      ) {
        return null
      }

      if (
        !isDry &&
        currentDistance !==
          parseInt(initialValues[StravaSettingKey.MAP_VISIBILITY], 10)
      ) {
        await updateCurrentDistance(
          parseInt(initialValues[StravaSettingKey.MAP_VISIBILITY], 10),
        )
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_MAP_VISIBILITY_FINISH,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: stravaUrls.startEndLocationUrl,
    action: async () => {
      window.location.reload()
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
